import React, { useEffect, useState } from "react";
import {
  Box, Card, CardContent, Typography, Button, Stack, Chip, TextField,
  Table, TableHead, TableBody, TableRow, TableCell, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Switch, FormControlLabel, Alert, IconButton
} from "@mui/material";
import { CloudUpload, Delete, Visibility, Send } from "@mui/icons-material";
import API from "../services/api";
import toast from "react-hot-toast";

export default function Campaigns() {
  const [list, setList] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "",
    body: "",
    image_url: "",
    click_url: "",
    show_on_web: true,
    show_on_mobile: true,
    send_push: false,
    end_at: ""
  });

  const load = async () => {
    try { setList((await API.get("/api/admin/campaigns")).data); }
    catch (e) { toast.error("Yüklenemedi"); }
  };

  useEffect(() => { load(); }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Maks 5 MB");

    setUploading(true);
    const fd = new FormData();
    fd.append("image", file);
    try {
      const r = await API.post("/api/admin/campaigns/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setForm({ ...form, image_url: r.data.url });
      toast.success("Görsel yüklendi");
    } catch (err) {
      toast.error("Yükleme hatası: " + (err.response?.data?.error || err.message));
    } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.title || !form.image_url) return toast.error("Başlık ve görsel zorunlu");
    if (!form.show_on_web && !form.show_on_mobile && !form.send_push) {
      return toast.error("En az bir kanal seç (web / mobil / push)");
    }
    setSaving(true);
    try {
      const r = await API.post("/api/admin/campaigns", form);
      const pushInfo = r.data.push ? ` | Push: ${r.data.push.sent || 0} cihaz` : "";
      toast.success(`Kampanya yayınlandı${pushInfo}`);
      setDialog(false);
      setForm({ title: "", body: "", image_url: "", click_url: "", show_on_web: true, show_on_mobile: true, send_push: false, end_at: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Hata");
    } finally { setSaving(false); }
  };

  const toggleActive = async (c) => {
    try {
      await API.put(`/api/admin/campaigns/${c.id}`, { is_active: !c.is_active });
      load();
    } catch (e) { toast.error("Hata"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Kampanya silinsin mi?")) return;
    try { await API.delete(`/api/admin/campaigns/${id}`); load(); }
    catch (e) { toast.error("Hata"); }
  };

  const fmt = (s) => s ? new Date(s).toLocaleDateString("tr-TR") : "—";

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={700}>📢 Kampanya / Broşür Yönetimi</Typography>
        <Button variant="contained" startIcon={<CloudUpload />} onClick={() => setDialog(true)} size="large">
          Yeni Kampanya
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        💡 Kampanya görselini yükle, web ana sayfasında popup olarak çıkar, mobil uygulamada push bildirim + uygulama açıkken in-app banner olarak görünür.
      </Alert>

      <Card>
        <CardContent>
          {list.length === 0 ? (
            <Alert severity="warning">Henüz kampanya yok. "Yeni Kampanya" butonuyla başla.</Alert>
          ) : (
            <Grid container spacing={2}>
              {list.map(c => (
                <Grid item xs={12} md={6} lg={4} key={c.id}>
                  <Card variant="outlined" sx={{ position: 'relative', opacity: c.is_active ? 1 : 0.5 }}>
                    <Box sx={{ height: 180, overflow: 'hidden', bgcolor: '#f5f5f5' }}>
                      <img src={c.image_url} alt={c.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </Box>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight={700}>{c.title}</Typography>
                      {c.body && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{c.body.substring(0, 100)}</Typography>}
                      <Stack direction="row" spacing={1} mb={1} flexWrap="wrap" gap={0.5}>
                        {c.show_on_web ? <Chip size="small" label="🌐 Web" color="primary" variant="outlined" /> : null}
                        {c.show_on_mobile ? <Chip size="small" label="📱 Mobil" color="secondary" variant="outlined" /> : null}
                        {c.send_push ? <Chip size="small" label={`🔔 Push (${c.push_sent_count || 0})`} color="success" variant="outlined" /> : null}
                      </Stack>
                      <Typography variant="caption" sx={{ display: 'block', color: '#888', mb: 1 }}>
                        Görüntülenme: <b>{c.view_count}</b> | Tıklama: <b>{c.click_count}</b><br />
                        Bitiş: {fmt(c.end_at)}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <FormControlLabel
                          control={<Switch checked={!!c.is_active} onChange={() => toggleActive(c)} size="small" />}
                          label="Aktif"
                          sx={{ flex: 1, m: 0 }}
                        />
                        <IconButton size="small" color="error" onClick={() => remove(c.id)}><Delete fontSize="small" /></IconButton>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialog} onClose={() => !saving && setDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>📢 Yeni Kampanya Oluştur</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField label="Başlık *" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} fullWidth />
            <TextField label="Açıklama / Mesaj" value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} fullWidth multiline rows={2} />

            <Box sx={{ border: '2px dashed #ccc', borderRadius: 2, p: 2, textAlign: 'center' }}>
              {form.image_url ? (
                <Box>
                  <img src={form.image_url} alt="" style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8 }} />
                  <Button size="small" onClick={() => setForm({...form, image_url: ""})}>Kaldır</Button>
                </Box>
              ) : (
                <Box>
                  <Button variant="contained" component="label" startIcon={<CloudUpload />} disabled={uploading}>
                    {uploading ? "Yükleniyor..." : "📷 Broşür / Görsel Seç"}
                    <input type="file" accept="image/*" hidden onChange={handleFile} />
                  </Button>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#888' }}>
                    JPG, PNG, WebP — Maks 5 MB
                  </Typography>
                </Box>
              )}
            </Box>

            <TextField
              label="Tıklama URL (opsiyonel)"
              value={form.click_url}
              onChange={(e) => setForm({...form, click_url: e.target.value})}
              fullWidth
              placeholder="https://miksanplastik.com.tr/kampanya/..."
              helperText="Kullanıcı banner'a tıklayınca açılacak link"
            />

            <TextField
              label="Bitiş Tarihi (opsiyonel)"
              type="datetime-local"
              value={form.end_at}
              onChange={(e) => setForm({...form, end_at: e.target.value})}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <Box sx={{ bgcolor: '#f5f7fa', p: 2, borderRadius: 1 }}>
              <Typography variant="subtitle2" mb={1}>Yayın Kanalları:</Typography>
              <Stack spacing={0}>
                <FormControlLabel
                  control={<Switch checked={form.show_on_web} onChange={(e) => setForm({...form, show_on_web: e.target.checked})} />}
                  label="🌐 Web sitesi ana sayfa popup"
                />
                <FormControlLabel
                  control={<Switch checked={form.show_on_mobile} onChange={(e) => setForm({...form, show_on_mobile: e.target.checked})} />}
                  label="📱 Mobil uygulamada in-app banner"
                />
                <FormControlLabel
                  control={<Switch checked={form.send_push} onChange={(e) => setForm({...form, send_push: e.target.checked})} />}
                  label="🔔 Mobil push bildirimi gönder (uygulama kapalıyken de gelir)"
                />
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)} disabled={saving}>İptal</Button>
          <Button variant="contained" startIcon={<Send />} onClick={save} disabled={saving || uploading || !form.image_url}>
            {saving ? "Yayınlanıyor..." : "Yayınla"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
